import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Switch, Redirect, useLocation } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme, alpha, darken, lighten } from "@mui/material/styles";
import { SnackbarProvider } from "notistack";

import "./App.css";
import "./material-icons.css";
import "./assets/roboto/roboto.css";

import { useAuth } from "./auth/AuthProvider";
import { useUser } from "./auth/UserProvider";
import MsalRedirectHandler from "./auth/MsalRedirectHandler";
import AppInsightsProvider from "./components/AppInsightsProvider";
import CustomRoute from "./components/CustomRoute";
import LoadingComponent from "./components/LoadingComponent";
import Logger from "./components/LoggerComponent";
import Notifier from "./components/Notifier";
import PrivateRoute from "./components/PrivateRoute";
import ProtectedRoute from "./components/ProtectedRoute";
import SideBar from "./components/Sidebar/Sidebar";
import TitleHelper from "./components/PathNameHelper";
import Annual from "./pages/Annual Budget/Annual";
import Analytics from "./pages/Administration/Analytics/Analytics";
import AnalyticsFiltered from "./pages/Administration/Analytics/AnalyticsFiltered";
import Filter from "./pages/Administration/Filter/Filter";
import FilterValue from "./pages/Administration/FilterValue/FilterValue";
import Groups from "./pages/Administration/Group/Groups";
import ManagementHr from "./pages/Administration/Hr Documents/ManagementHr";
import HrInfo from "./pages/Administration/Hr Info/HrInfo";
import Layout from "./pages/Administration/Layout/Layout";
import MenuItem from "./pages/Administration/MenuItem/MenuItem";
import Source from "./pages/Administration/Source/Source";
import Tab from "./pages/Administration/Tab/Tab";
import Home from "./pages/Home";
import Audit from "./pages/Audit";
import FinancialClosure from "./pages/FinancialClosure";
import HR from "./pages/HR/Hr";
import ProviderLayout from "./pages/ProviderLayout";
import PRE from "./pages/PRE/PRE";
import { SearchUser } from "./pages/SearchUserDomain/SearchUser";
import { SecurityMenuItem } from "./pages/Security/Security";
import { Unauthorized } from "./pages/Unauthorized";
import { NotFound } from "./pages/NotFound";
import { useStylesLayout } from "./styles/Layout";
import Mstr from "./pages/Mstr";

const theme = (() => {
  const appTheme = createTheme({
    palette: {
      primary: {
        main: "rgb(2, 31, 71)",
      },
      secondary: {
        main: "#666666",
      },
      action: {
        selected: "rgba(76, 175, 80, 0.6)",
        hover: "rgba(107, 232, 112, 0.3)",
        disabled: "#9B9B9B",
      },
    },
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            width: "min(620px, calc(100% - 24px))",
            margin: "12px",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "10px 14px",
            fontSize: "1.05rem",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "10px 14px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "8px 12px",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: "rgba(76, 175, 80, 0.8)",
              fontWeight: "bolder",
            },
          },
        },
      },
    },
  });

  // Compatibility shim for @material-table/core when MUI internals read these helpers from theme.
  appTheme.alpha = alpha;
  appTheme.darken = darken;
  appTheme.lighten = lighten;

  return appTheme;
})();

function AppRoutes() {
  const { isInitialized } = useAuth();
  const { loading: userLoading } = useUser();
  const location = useLocation();

  if (!isInitialized) {
    return <LoadingComponent loading />;
  }

  if (location.pathname.includes("/auth/callback")) {
    return <MsalRedirectHandler />;
  }

  if (userLoading) {
    return <LoadingComponent loading />;
  }

  return (
    <Switch>
      <ProtectedRoute exact strict path="/" component={Home} />
      <ProtectedRoute exact component={Unauthorized} path="/Unauthorized" />
      <ProtectedRoute exact component={NotFound} path="/NotFound" />
      <ProtectedRoute exact component={Home} path="/Home" />
      <CustomRoute exact component={HR} path="/HR/Documents" />
      <CustomRoute exact component={SecurityMenuItem} path="/Administration/Security" />
      <ProtectedRoute exact component={SearchUser} path="/SearchUser" />
      <PrivateRoute exact component={Filter} path="/Administration/Filter" />
      <PrivateRoute exact component={Layout} path="/Administration/Layout" />
      <PrivateRoute exact component={MenuItem} path="/Administration/MenuItem" />
      <PrivateRoute exact component={Source} path="/Administration/Source" />
      <PrivateRoute exact component={Tab} path="/Administration/Tab" />
      <PrivateRoute exact component={ManagementHr} path="/Administration/Hr" />
      <PrivateRoute exact component={HrInfo} path="/Administration/Hrinfo/:hrId?" />
      <PrivateRoute exact component={Analytics} path="/Administration/Analytics/:id?" />
      <PrivateRoute exact component={AnalyticsFiltered} path="/AnalyticsFiltered/:id?" />
      <CustomRoute exact component={FinancialClosure} path="/Administration/FinancialClosure" />
      <PrivateRoute exact component={Groups} path="/Administration/Groups" />
      <PrivateRoute exact component={FilterValue} path="/Administration/FilterValue/:filterId?" />
      <PrivateRoute exact forOwner component={Audit} path="/Audit/:id?" />
      <ProtectedRoute exact strict path="/Layout/:id" component={ProviderLayout} />
      <CustomRoute exact component={PRE} path="/PRE" />
      <CustomRoute exact component={Annual} path="/Annual" />
      <ProtectedRoute path="/mstr" component={Mstr} />
      <Redirect to="/" />
    </Switch>
  );
}

function AppContent() {
  const classes = useStylesLayout();
  const [isInitialBootLoading, setIsInitialBootLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialBootLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  if (isInitialBootLoading) {
    return <LoadingComponent loading withoutNavBar />;
  }

  return (
    <SnackbarProvider dense maxSnack={3}>
      <Router basename="/Reports/">
        <TitleHelper />
        <AppInsightsProvider>
          <div className={classes.root}>
            <CssBaseline />
            <SideBar />
            <div className={classes.content}>
              <Notifier />
              <Logger />
              <AppRoutes />
            </div>
          </div>
        </AppInsightsProvider>
      </Router>
    </SnackbarProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <AppContent />
    </ThemeProvider>
  );
}
